import { call } from '@advanza/api'
import { Button, LoadingDots, PreIcon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import { useToast } from 'components/misc/Toaster'
import DemoRequestConfirmModal, {
    DEMO_REQUEST_ACTION_CREATE,
    DEMO_REQUEST_ACTION_REMOVE,
} from 'components/providers/DemoRequestConfirmModal'
import FormattedTime from 'components/ui/FormattedTime'
import { useCallback, useEffect, useState } from 'react'

interface DemoRequestButtonProps {
    providerId: number
}

const DemoRequestButton = ({ providerId }: DemoRequestButtonProps) => {
    const { addToast } = useToast()
    const [hasDemoRequest, setHasDemoRequest] = useState(false)
    const [expiresAt, setExpiresAt] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isFetching, setIsFetching] = useState(true)

    const fetchStatus = useCallback(async () => {
        try {
            const response = await call(`office/providers/has-demo-request/${providerId}`)
            if (response?.status === 'success') {
                setHasDemoRequest(response.hasDemoRequest)
                setExpiresAt(response.expiresAt)
            }
        } catch {
            // Silently fail on initial fetch
        } finally {
            setIsFetching(false)
        }
    }, [providerId])

    useEffect(() => {
        fetchStatus()
    }, [fetchStatus])

    const handleCreate = async () => {
        setIsLoading(true)
        try {
            const response = await call(`office/providers/create-demo-request/${providerId}`, {
                method: 'POST',
            })

            if (response?.status === 'success') {
                setHasDemoRequest(true)
                setExpiresAt(response.expiresAt)
                addToast({
                    msg: 'Demo request created successfully',
                    green: true,
                    icon: 'check_circle',
                })
            } else {
                throw new Error(response?.message || 'Unknown error')
            }
        } catch (error) {
            addToast({
                msg: `Error creating demo request: ${error instanceof Error ? error.message : String(error)}`,
                red: true,
                icon: 'error',
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleRemove = async () => {
        setIsLoading(true)
        try {
            const response = await call(`office/providers/remove-demo-request/${providerId}`, {
                method: 'POST',
            })

            if (response?.status === 'success') {
                setHasDemoRequest(false)
                setExpiresAt(null)
                addToast({
                    msg: 'Demo request removed successfully',
                    green: true,
                    icon: 'check_circle',
                })
            } else {
                throw new Error(response?.message || 'Unknown error')
            }
        } catch (error) {
            addToast({
                msg: `Error removing demo request: ${error instanceof Error ? error.message : String(error)}`,
                red: true,
                icon: 'error',
            })
        } finally {
            setIsLoading(false)
        }
    }

    const onClickCreate = () => {
        NiceModal.show(DemoRequestConfirmModal, {
            action: DEMO_REQUEST_ACTION_CREATE,
            onConfirm: handleCreate,
        })
    }

    const onClickRemove = () => {
        NiceModal.show(DemoRequestConfirmModal, {
            action: DEMO_REQUEST_ACTION_REMOVE,
            onConfirm: handleRemove,
        })
    }

    if (isFetching) {
        return <LoadingDots />
    }

    return (
        <>
            {hasDemoRequest ? (
                <PreIcon icon="campaign" fontSize={18}>
                    <span style={{ color: '#9C27B0' }}>
                        Request Demo active
                        {expiresAt && (
                            <>
                                {' — expires '}
                                <FormattedTime date={expiresAt} format="dd-MM-yyyy HH:mm" />
                            </>
                        )}
                    </span>{' '}
                    <Button name="link" disabled={isLoading} onClick={onClickRemove}>
                        Remove
                    </Button>
                </PreIcon>
            ) : (
                <Button name="link" disabled={isLoading} onClick={onClickCreate}>
                    <PreIcon icon="campaign" fontSize={18}>
                        Create Demo Request
                    </PreIcon>
                </Button>
            )}
        </>
    )
}

export default DemoRequestButton
